import type { Fiat, Lang } from '@onyx/shared';

const locale = (lang: Lang) => (lang === 'ru' ? 'ru-RU' : 'en-US');

export interface NumberParts {
  sign: string;
  int: string;
  sep: string;
  frac: string;
  /** Currency symbol and its side, for fiat. */
  symbol: string;
  symbolFirst: boolean;
}

const fiatFormatters = new Map<string, Intl.NumberFormat>();

export function fiatParts(value: number, fiat: Fiat, lang: Lang): NumberParts {
  const key = `${fiat}:${lang}`;
  let f = fiatFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale(lang), { style: 'currency', currency: fiat, minimumFractionDigits: 2, maximumFractionDigits: 2, currencyDisplay: 'narrowSymbol' });
    fiatFormatters.set(key, f);
  }
  const parts = f.formatToParts(value);
  const out: NumberParts = { sign: '', int: '', sep: '', frac: '', symbol: '', symbolFirst: false };
  let seenNumber = false;
  for (const p of parts) {
    if (p.type === 'minusSign') out.sign = '−';
    else if (p.type === 'integer' || p.type === 'group') {
      out.int += p.type === 'group' ? ' ' : p.value;
      seenNumber = true;
    } else if (p.type === 'decimal') out.sep = p.value;
    else if (p.type === 'fraction') out.frac = p.value;
    else if (p.type === 'currency') {
      out.symbol = p.value;
      out.symbolFirst = !seenNumber;
    }
  }
  return out;
}

export function formatFiat(value: number, fiat: Fiat, lang: Lang): string {
  const p = fiatParts(value, fiat, lang);
  const num = `${p.sign}${p.int}${p.sep}${p.frac}`;
  return p.symbolFirst ? `${p.symbol}${num}` : `${num} ${p.symbol}`;
}

/** Display precision for crypto: stables 2, large values 4, small values 6 significant digits. */
export function displayDigits(value: number, stable: boolean): number {
  const abs = Math.abs(value);
  if (stable) return 2;
  if (abs === 0) return 2;
  if (abs >= 1000) return 2;
  if (abs >= 1) return 4;
  return Math.min(9, Math.max(2, Math.ceil(-Math.log10(abs)) + 4));
}

/** Split a decimal string into grouped integer and trimmed fraction without float rounding. */
export function cryptoParts(amount: string, maxFrac: number, lang: Lang, keepZeros = false): NumberParts {
  const neg = amount.startsWith('-');
  const [whole = '0', fracRaw = ''] = (neg ? amount.slice(1) : amount).split('.');
  let frac = fracRaw.slice(0, maxFrac);
  if (!keepZeros) frac = frac.replace(/0+$/, '');
  const int = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return { sign: neg ? '−' : '', int, sep: frac ? (lang === 'ru' ? ',' : '.') : '', frac, symbol: '', symbolFirst: false };
}

export function formatCrypto(amount: string, maxFrac: number, lang: Lang): string {
  const p = cryptoParts(amount, maxFrac, lang);
  return `${p.sign}${p.int}${p.sep}${p.frac}`;
}

export function formatRate(value: number, lang: Lang): string {
  const digits = value >= 100 ? 2 : value >= 1 ? 4 : Math.min(10, Math.ceil(-Math.log10(value)) + 3);
  return new Intl.NumberFormat(locale(lang), { maximumFractionDigits: digits }).format(value);
}

export function formatPercent(share: number, lang: Lang): string {
  const pct = share * 100;
  return `${new Intl.NumberFormat(locale(lang), { maximumFractionDigits: pct < 1 ? 1 : 0 }).format(pct < 0.1 && pct > 0 ? 0.1 : pct)}%`;
}

export function formatDay(iso: string, lang: Lang, today: string, yesterday: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = (startOf(now) - startOf(d)) / 86400000;
  if (diff === 0) return today;
  if (diff === 1) return yesterday;
  return d.toLocaleDateString(locale(lang), { day: 'numeric', month: 'long', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

export const formatTime = (iso: string, lang: Lang) => new Date(iso).toLocaleTimeString(locale(lang), { hour: '2-digit', minute: '2-digit' });
export const formatDateTime = (iso: string, lang: Lang) =>
  new Date(iso).toLocaleString(locale(lang), { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

export function shortAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-6)}` : address;
}

/** Normalise keypad / field input into a decimal string with at most `decimals` places. */
export function cleanAmountInput(raw: string, decimals: number): string {
  let s = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot >= 0) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '').slice(0, decimals);
  if (s.startsWith('.')) s = `0${s}`;
  s = s.replace(/^0+(?=\d)/, '');
  return s.slice(0, 18);
}
